import { useEffect } from "react";

const GTAG_ID = "AW-18228122458";
const JSON_LD_ID = "cvfolks-jsonld";

export function usePageMeta(service) {
  useEffect(() => {
    if (!service) return;
    const { seo, jsonLd } = service;

    if (seo?.title) document.title = seo.title;

    const ensure = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [, attrName, attrVal] = selector.match(/\[(.+?)="(.+?)"\]/) || [];
        if (attrName) el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (seo?.description)
      ensure('meta[name="description"]', "content", seo.description);
    if (seo?.keywords)
      ensure('meta[name="keywords"]', "content", seo.keywords);

    // Canonical link — helps Google dedupe SPA routes.
    if (typeof window !== "undefined") {
      const canonical = window.location.origin + window.location.pathname;
      let linkEl = document.head.querySelector('link[rel="canonical"]');
      if (!linkEl) {
        linkEl = document.createElement("link");
        linkEl.setAttribute("rel", "canonical");
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute("href", canonical);
    }

    // JSON-LD structured data — one <script> per page, replaced on nav.
    // Pages can pass either a single object or an array of schemas.
    const prev = document.getElementById(JSON_LD_ID);
    if (prev) prev.remove();
    if (jsonLd) {
      const payload = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      const script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.id = JSON_LD_ID;
      script.textContent = JSON.stringify(
        payload.length === 1 ? payload[0] : payload,
      );
      document.head.appendChild(script);
    }

    // Fire Google Ads page_view for SPA route changes so each service page
    // is tracked as a distinct page view.
    if (typeof window.gtag === "function") {
      window.gtag("config", GTAG_ID, {
        page_path: window.location.pathname + window.location.search,
        page_title: seo?.title || document.title,
      });
    }
  }, [service]);
}

// ---------------- Shared JSON-LD builders ---------------- //

/** Global Organization schema — safe to include on every page. */
export const ORG_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "CV Folks",
  url: "https://cvfolksbritain.com",
  telephone: "+44 7448 457089",
  email: "cvfolksbritain@gmail.com",
  logo: "https://cvfolksbritain.com/img/logo.png",
  image: "https://cvfolksbritain.com/img/logo.png",
  priceRange: "£££",
  areaServed: "United Kingdom",
  address: {
    "@type": "PostalAddress",
    streetAddress: "354 Gray's Inn Rd",
    addressLocality: "London",
    postalCode: "WC1X 8BP",
    addressCountry: "GB",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "150",
    bestRating: "5",
    worstRating: "1",
  },
  sameAs: [
    "https://www.linkedin.com/company/cv-folks",
    "https://www.trustpilot.com/review/cvfolks.co.uk",
  ],
};

/** Product / Service Offer schema for a specific package. */
export function productJsonLd({ id, name, price, description, currency = "GBP" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "CV Writing Service",
    name,
    description,
    provider: {
      "@type": "Organization",
      name: "CV Folks",
      url: "https://cvfolksbritain.com",
    },
    areaServed: { "@type": "Country", name: "United Kingdom" },
    offers: {
      "@type": "Offer",
      url: `https://cvfolksbritain.com/checkout/${id}`,
      priceCurrency: currency,
      price: String(price),
      availability: "https://schema.org/InStock",
    },
  };
}

/** BreadcrumbList — for deep pages like articles or service subpages. */
export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

/** Article schema for the /career-resources/<slug> pages. */
export function articleJsonLd({ title, description, slug, updated }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    author: { "@type": "Organization", name: "CV Folks" },
    publisher: {
      "@type": "Organization",
      name: "CV Folks",
      logo: {
        "@type": "ImageObject",
        url: "https://cvfolksbritain.com/img/logo.png",
      },
    },
    datePublished: updated,
    dateModified: updated,
    mainEntityOfPage: `https://cvfolksbritain.com/career-resources/${slug}`,
  };
}
